import { create } from 'zustand'
import {
  DashboardSummary,
  EmvTrendResponse,
  AccountEmvResponse,
  AssetTypeEmvResponse,
  AssetEmvResponse,
  TopTagsResponse,
  ActiveActivationsResponse,
  DashboardPeriod,
  DashboardSortBy,
  DashboardSortOrder
} from '../types/activationDashboard'
import { activationDashboardService } from '../services/activationDashboardService'

interface ActivationDashboardState {
  // State
  summary: DashboardSummary | null
  emvTrend: EmvTrendResponse | null
  accountEmv: AccountEmvResponse | null
  assetTypeEmv: AssetTypeEmvResponse | null
  assetEmv: AssetEmvResponse | null
  topTags: TopTagsResponse | null
  activeActivations: ActiveActivationsResponse | null

  // Loading states (granular for parallel loading)
  loadingSummary: boolean
  loadingEmvTrend: boolean
  loadingAccountEmv: boolean
  loadingAssetTypeEmv: boolean
  loadingAssetEmv: boolean
  loadingTopTags: boolean
  loadingActiveActivations: boolean
  loadingAll: boolean

  // Error states
  error: string | null

  // Filter state
  period: DashboardPeriod
  customDateRange: { start: string; end: string } | null
  sortBy: DashboardSortBy
  sortOrder: DashboardSortOrder

  // Actions
  setPeriod: (period: DashboardPeriod) => void
  setCustomDateRange: (range: { start: string; end: string } | null) => void
  setSortBy: (sortBy: DashboardSortBy) => void
  setSortOrder: (sortOrder: DashboardSortOrder) => void

  loadSummary: (customerId: string, period?: DashboardPeriod, customDateRange?: { start: string; end: string } | null) => Promise<void>
  loadEmvTrend: (customerId: string, period?: DashboardPeriod, customDateRange?: { start: string; end: string } | null) => Promise<void>
  loadAccountEmv: (customerId: string, period?: DashboardPeriod, customDateRange?: { start: string; end: string } | null) => Promise<void>
  loadAssetTypeEmv: (customerId: string, period?: DashboardPeriod, customDateRange?: { start: string; end: string } | null) => Promise<void>
  loadTopTags: (customerId: string, period?: DashboardPeriod, limit?: number, customDateRange?: { start: string; end: string } | null) => Promise<void>
  loadActiveActivations: (
    customerId: string,
    sortBy?: DashboardSortBy,
    sortOrder?: DashboardSortOrder
  ) => Promise<void>
  loadAllDashboardData: (customerId: string, period?: DashboardPeriod, customDateRange?: { start: string; end: string } | null) => Promise<void>

  reset: () => void
}

const useActivationDashboardStore = create<ActivationDashboardState>((set, get) => ({
  // Initial State
  summary: null,
  emvTrend: null,
  accountEmv: null,
  assetTypeEmv: null,
  assetEmv: null,
  topTags: null,
  activeActivations: null,

  loadingSummary: false,
  loadingEmvTrend: false,
  loadingAccountEmv: false,
  loadingAssetTypeEmv: false,
  loadingAssetEmv: false,
  loadingTopTags: false,
  loadingActiveActivations: false,
  loadingAll: false,

  error: null,

  period: '90days',
  customDateRange: null,
  sortBy: 'status',
  sortOrder: 'desc',

  // Filter Actions
  setPeriod: (period: DashboardPeriod) => {
    set({ period })
  },

  setCustomDateRange: (range: { start: string; end: string } | null) => {
    set({ customDateRange: range })
  },

  setSortBy: (sortBy: DashboardSortBy) => {
    set({ sortBy })
  },

  setSortOrder: (sortOrder: DashboardSortOrder) => {
    set({ sortOrder })
  },

  // Data Loading Actions
  loadSummary: async (customerId: string, period?: DashboardPeriod, customDateRange?: { start: string; end: string } | null) => {
    const effectivePeriod = period || get().period
    const effectiveCustomDateRange = customDateRange !== undefined ? customDateRange : get().customDateRange
    set({ loadingSummary: true, error: null })
    try {
      const summary = await activationDashboardService.getDashboardSummary(
        customerId,
        effectivePeriod,
        effectiveCustomDateRange
      )
      set({ summary, loadingSummary: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load dashboard summary',
        loadingSummary: false
      })
    }
  },

  loadEmvTrend: async (customerId: string, period?: DashboardPeriod, customDateRange?: { start: string; end: string } | null) => {
    const effectivePeriod = period || get().period
    const effectiveCustomDateRange = customDateRange !== undefined ? customDateRange : get().customDateRange
    set({ loadingEmvTrend: true, error: null })
    try {
      const emvTrend = await activationDashboardService.getEmvTrend(
        customerId,
        effectivePeriod,
        effectiveCustomDateRange
      )
      set({ emvTrend, loadingEmvTrend: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load EMV trend',
        loadingEmvTrend: false
      })
    }
  },

  loadAccountEmv: async (customerId: string, period?: DashboardPeriod, customDateRange?: { start: string; end: string } | null) => {
    const effectivePeriod = period || get().period
    const effectiveCustomDateRange = customDateRange !== undefined ? customDateRange : get().customDateRange
    set({ loadingAccountEmv: true, error: null })
    try {
      const accountEmv = await activationDashboardService.getEmvByAccount(
        customerId,
        effectivePeriod,
        effectiveCustomDateRange
      )
      set({ accountEmv, loadingAccountEmv: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load account EMV',
        loadingAccountEmv: false
      })
    }
  },

  loadAssetTypeEmv: async (customerId: string, period?: DashboardPeriod, customDateRange?: { start: string; end: string } | null) => {
    const effectivePeriod = period || get().period
    const effectiveCustomDateRange = customDateRange !== undefined ? customDateRange : get().customDateRange
    set({ loadingAssetTypeEmv: true, error: null })
    try {
      const assetTypeEmv = await activationDashboardService.getEmvByAssetType(
        customerId,
        effectivePeriod,
        effectiveCustomDateRange
      )
      set({ assetTypeEmv, loadingAssetTypeEmv: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load asset type EMV',
        loadingAssetTypeEmv: false
      })
    }
  },

  loadTopTags: async (customerId: string, period?: DashboardPeriod, limit: number = 5, customDateRange?: { start: string; end: string } | null) => {
    const effectivePeriod = period || get().period
    const effectiveCustomDateRange = customDateRange !== undefined ? customDateRange : get().customDateRange
    set({ loadingTopTags: true, error: null })
    try {
      const topTags = await activationDashboardService.getTopTags(
        customerId,
        effectivePeriod,
        limit,
        effectiveCustomDateRange
      )
      set({ topTags, loadingTopTags: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load top tags',
        loadingTopTags: false
      })
    }
  },

  loadActiveActivations: async (
    customerId: string,
    sortBy?: DashboardSortBy,
    sortOrder?: DashboardSortOrder
  ) => {
    const effectiveSortBy = sortBy || get().sortBy
    const effectiveSortOrder = sortOrder || get().sortOrder
    set({ loadingActiveActivations: true, error: null })
    try {
      const activeActivations = await activationDashboardService.getActiveActivations(
        customerId,
        effectiveSortBy,
        effectiveSortOrder
      )
      set({ activeActivations, loadingActiveActivations: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load active activations',
        loadingActiveActivations: false
      })
    }
  },

  loadAllDashboardData: async (customerId: string, period?: DashboardPeriod, customDateRange?: { start: string; end: string } | null) => {
    const effectivePeriod = period || get().period
    const effectiveCustomDateRange = customDateRange !== undefined ? customDateRange : get().customDateRange
    set({
      loadingAll: true,
      loadingSummary: true,
      loadingEmvTrend: true,
      loadingAccountEmv: true,
      loadingAssetTypeEmv: true,
      loadingAssetEmv: true,
      loadingTopTags: true,
      loadingActiveActivations: true,
      error: null
    })

    try {
      const data = await activationDashboardService.getAllDashboardData(
        customerId,
        effectivePeriod,
        effectiveCustomDateRange
      )

      set({
        summary: data.summary,
        emvTrend: data.emvTrend,
        accountEmv: data.accountEmv,
        assetTypeEmv: data.assetTypeEmv,
        assetEmv: data.assetEmv,
        topTags: data.topTags,
        activeActivations: data.activeActivations,
        loadingAll: false,
        loadingSummary: false,
        loadingEmvTrend: false,
        loadingAccountEmv: false,
        loadingAssetTypeEmv: false,
        loadingAssetEmv: false,
        loadingTopTags: false,
        loadingActiveActivations: false
      })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load dashboard data',
        loadingAll: false,
        loadingSummary: false,
        loadingEmvTrend: false,
        loadingAccountEmv: false,
        loadingAssetTypeEmv: false,
        loadingAssetEmv: false,
        loadingTopTags: false,
        loadingActiveActivations: false
      })
    }
  },

  reset: () =>
    set({
      summary: null,
      emvTrend: null,
      accountEmv: null,
      assetTypeEmv: null,
      assetEmv: null,
      topTags: null,
      activeActivations: null,
      loadingSummary: false,
      loadingEmvTrend: false,
      loadingAccountEmv: false,
      loadingAssetTypeEmv: false,
      loadingAssetEmv: false,
      loadingTopTags: false,
      loadingActiveActivations: false,
      loadingAll: false,
      error: null,
      period: '90days',
      customDateRange: null,
      sortBy: 'status',
      sortOrder: 'desc'
    })
}))

export default useActivationDashboardStore
